import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

/**
 * Matches TailAdmin's underline tabs: a horizontal tablist on a bottom border,
 * inactive items in gray with hover, the active item in brand colour with a
 * `border-b-2 border-brand-500` underline, and the panel below.
 */
export function Tabs({ items, value, onChange, className }: TabsProps) {
  const active = items.find((item) => item.id === value) ?? items[0];

  return (
    <div className={cn('w-full', className)}>
      <div
        role="tablist"
        aria-label="Sections"
        className="flex flex-wrap gap-x-1 border-b border-gray-200 dark:border-white/10"
      >
        {items.map((item) => {
          const selected = item.id === active?.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`tab-${item.id}`}
              aria-selected={selected}
              aria-controls={`panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(item.id)}
              className={cn(
                '-mb-px inline-flex items-center border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                selected
                  ? 'border-brand-500 text-brand-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {active && (
        <div
          role="tabpanel"
          id={`panel-${active.id}`}
          aria-labelledby={`tab-${active.id}`}
          className="pt-6"
        >
          {active.content}
        </div>
      )}
    </div>
  );
}
